/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
	Container, Col, Row, Form, FormSelect, Modal
} from 'react-bootstrap';
import { toast } from 'react-toastify';

import { UsePortalDispatch, UsePortalSelector } from '../../Store';
import { selectIsDistributorAdmin } from '../../Store/user/userPermissions';

import Button from '../../Parts/Button';
import {
	useDistributorLocationsByDistributorId,
	useDistributors,
	useRepresentatives,
	useSalesPeople,
	useTerritoryMetaById
} from '../../Store/entities/hooks';
import { useActiveRMATypes, useAllRMATypes } from '../../Store/entities/rmaTypes/hooks';
import { useIsEngineering, useIsPowerShadesAdmin, useIsService } from '../../hooks';
import { captureSentryError, isFailedApiCall } from '../../psUtil';
import { changeQuoteMultiplier } from '../../Store/entities/multipliers';
import { UseMultipliersByQuoteId, useQuoteById, useQuoteTypeNameByQuoteId } from '../../Store/entities/quotes/hooks';
import { loadQuoteById, repriceQuote, updateQuoteMeta } from '../../Store/entities/quotes';
import { setAssembliesUnlocked, updateAssemblies } from '../../Store/entities/assemblies';
import { useAssembliesByQuoteId } from '../../Store/entities/assemblies/hooks';
import { usePortfolios } from '../../Store/entities/portfolios/hooks';
import { updateQuotePortfolioCode } from '../../Store/entities/portfolios';
import { useUser } from '../../Store/user/hooks';
import apiCalls from '../../PowerShadesAPIFunctions';
import { fakeRootType } from '../../FakeRootType';
import { useCurrentMultiplier } from '../../Store/entities/multipliers/hooks';

type QuoteLevelVariablesProps = {
	quoteId: number;
	ROOT: fakeRootType;
};

const QuoteLevelVariables = ({ quoteId, ROOT }: QuoteLevelVariablesProps) => {
	const quote = useQuoteById(quoteId);
	const isEditable = quote.editable ?? false;
	const userData = useUser();

	const [open, setOpen] = useState(false);
	const representatives = useRepresentatives();
	const salespeople = useSalesPeople();
	
	const distId = quote.distributor_id ?? 0;
	
	const [convertingToDemo, setConvertingToDemo] = useState(false);

	const distributors = useDistributors();

	const isOrder = quote?.order_status !== "active_quote";

	const isPsAdmin = useIsPowerShadesAdmin();

	const quoteType = useQuoteTypeNameByQuoteId(quoteId);
	const isDemo = quoteType.toLocaleLowerCase() === 'Demo';

	const distributorLocations = useDistributorLocationsByDistributorId(distId);

	const dispatch = UsePortalDispatch();

	const territory = useTerritoryMetaById(quote.territory_id ?? 0);

	const isDistributorAdmin = useSelector(selectIsDistributorAdmin);

	const isEvolution
		= userData?.roles?.some((role) => role?.entity_name?.includes('Evolution')) ?? false;

	const hasAccessToUnlockAndReprice = (() => {
		if (isPsAdmin) {
			return true;
		}
		if (isEvolution && isDistributorAdmin) {
			return true;
		}
		return false;
	})();

	const sortByName = (a, b) => {
		if (a.name < b.name) {
			return -1;
		}
		if (a.name > b.name) {
			return 1;
		}
		return 0;
	};

	const shippingOptions: JSX.Element[] = [];

	const willShip = quote.ship_order ?? false;
	shippingOptions.push(<option key="donotship" value={0} selected={willShip}>
		Will-Call, Pick up in Joplin, MO
	</option>);
	shippingOptions.push(<option key="ship" value={1} selected={willShip}>
		Ship this quote
	</option>);

	const whipShippingOptions: JSX.Element[] = [];

	whipShippingOptions.push(<option key="donotpreshipwhips" value={0} selected={!willShip}>
		Ship with entire order
	</option>);
	whipShippingOptions.push(<option key="preshipwhips" value={1} selected={willShip}>
		Ship before entire order
	</option>);

	let distOptions: JSX.Element[] = [];

	distOptions.push(<option key={distId} selected={distId === 0 || distId === null} value={0}>
		None
	</option>);

	if (distributors) {
		const adds = distributors.sort(sortByName).map((d) => (
			<option key={d.id} selected={d.id === distId} value={d.id}>
				{d.name}
			</option>
		));

		distOptions = distOptions.concat(adds);
	}
	let distLocationOptions: JSX.Element[] = [];
	const distLocationId = quote.distributor_location_id ?? 0;
	distLocationOptions.push(<option
		key={distLocationId}
		selected={distLocationId === 0 || distLocationId === null}
		value={0}
	>
		None
	</option>);

	if (distributorLocations) {
		const adds = distributorLocations.sort(sortByName).map((d) => (
			<option key={d.id} selected={d.id === distId} value={d.id}>
				{d.name}
			</option>
		));

		distLocationOptions = distLocationOptions.concat(adds);
	}

	let repOptions: JSX.Element[] = [];
	const repId = quote.representative_id ?? 0;
	repOptions.push(<option key="0-none" selected={repId === 0 || repId === null}>
		None
	</option>);

	if (representatives) {
		const adds = representatives.sort(sortByName).map((d) => (
			<option key={d.id} selected={d.id === repId} value={d.id}>
				{d.name}
			</option>
		));

		repOptions = repOptions.concat(adds);
	}

	const currentMultiplier = useCurrentMultiplier(quoteId);
	const currentPricing = currentMultiplier?.name ?? 'MSRP';

	const costOptions: JSX.Element[] = [];
	costOptions.push(<option key="MSRP" value="MSRP" selected={currentPricing === 'MSRP'}>
		Price
	</option>);

	const isSalesperson = UsePortalSelector((state) =>
		state.user?.roles?.some((r1) => r1.role_name === 'dealer_salesperson'));

	if (!isSalesperson) {
		costOptions.push(<option key="pricing" value="buy" selected={currentPricing === 'buy'}>
			My Price
		</option>);
	}

	if ((territory?.msrp_multiplier ?? 1) !== 1) {
		const territoryCurrency = territory?.iso_4217_currency_code_sell;
		costOptions.push(<option
			key="non-us-currency"
			value={`MSRP-${territoryCurrency}`}
			selected={currentPricing === 'non-us-currency'}
		>
			{`Price (${territoryCurrency})`}
		</option>);
	}

	const quoteMultipliers = UseMultipliersByQuoteId(quoteId);

	const costOptions2 = quoteMultipliers?.list.map((qm) => (
		<option key={qm.name} value={qm.name} selected={currentMultiplier.name === qm.name}>
			{qm.name}
		</option>
	)) ?? [];

	const salespersonItems: JSX.Element[] = [];

	salespeople?.forEach((sp) => {
		salespersonItems.push(<option key={sp.id} value={sp.id} selected={quote.sales_person_id === sp.id}>
			{sp.name}
		</option>);
	});

	const [localPreviousQuoteId, setLocalPreviousQuoteId] = useState(quote.previous_quote_id ?? 0);
	const [editingPreviousQuoteId, setEditingPreviousQuoteId] = useState(false);

	useEffect(() => {
		if (quote.previous_quote_id !== localPreviousQuoteId && !editingPreviousQuoteId) {
			setLocalPreviousQuoteId(quote.previous_quote_id ?? 0);
		}
	}, [editingPreviousQuoteId, quote.previous_quote_id]);

	const localPreviousQuoteSave = () => {
		dispatch(updateQuoteMeta({
			quoteId,
			quoteVM: {
				previous_quote_id: localPreviousQuoteId,
			}
		})).then((resp) => {
			if (resp.meta.requestStatus !== 'fulfilled') {
				// Set error;
				ROOT.alert({
					text: 'Error saving previous quote id',
					title: 'Quote Id Error',
					quickFormat: 'error',
				})
					.then(() => {
						// If bad it back to it's base
						setLocalPreviousQuoteId(quote.previous_quote_id ?? 0);
						setEditingPreviousQuoteId(false);
					})
					.catch(() => undefined);
			} else {
				// else set it locally
				setLocalPreviousQuoteId(localPreviousQuoteId);
				setEditingPreviousQuoteId(false);
			}
		});
	};

	const localPreviousQuoteCancel = () => {
		// set it back to it's base
		setLocalPreviousQuoteId(quote.previous_quote_id ?? 0);

		// stop edit mode
		setEditingPreviousQuoteId(false);
	};

	const demoizeQuote = () => {
		setConvertingToDemo(true);
		ROOT.alert({
			text: 'Make this Quote a Demo Quote?',
			title: 'Demo Quote',
			acceptButtonText: 'Make Demo',
			onAccept: () => {
				apiCalls.makeQuoteDemo(quoteId).then((resp) => {
					if (isFailedApiCall(resp)) {
						captureSentryError(new Error('Quote conversion to demo cancelled!'));
						toast.error('Quote conversion to demo failed');
						setConvertingToDemo(false);
						return;
					}
					toast.success('Quote converted to demo successfully');
					setConvertingToDemo(false);
					setOpen(false);
					dispatch(loadQuoteById(quoteId));
				});
			},
			onCancel: () => {
				setConvertingToDemo(false);
				toast.info('Quote conversion to demo cancelled');
			}
		});
	};

	const lightGapOptions = [
		<option key="lightgapdisabled" value="false">
			Disabled
		</option>,
		<option key="lightgapenabled" value="true">
			Enabled
		</option>,
	];

	const railrodPreventionOptions = [
		<option key="railroadenabled" value="true">
			Enabled
		</option>,
		<option key="railroaddisabled" value="false">
			Disabled
		</option>,
	];

	const assemblies = useAssembliesByQuoteId(quoteId);
	const assyLineNumbers = assemblies.map((a) => a.sequence_id);

	const UnlockAndRepriceEachShade = () => {
		dispatch(repriceQuote({ quoteId }));
		dispatch(setAssembliesUnlocked({
			quoteId,
			unlocked: true,
			sequenceIds: assyLineNumbers
		}));
		setOpen(false);
		ROOT.toast('Shades unlocked and repricing...', { type: 'success' });
	};

	const allRMATypes = useAllRMATypes();
	const activeRMATypes = useActiveRMATypes();
	const RMATypes = isOrder ? allRMATypes : activeRMATypes;
	const isService = useIsService();

	const rmaTypeOptions = RMATypes.map((rmaType) => (
		<option key={rmaType.id} value={rmaType.id}>
			{rmaType.name}
		</option>
	));

	const rmaTypeId = quote.rma_type_id ?? 0;

	// Hardcoded one for now, we'll redux it later.
	const isRma = quote?.quote_type_id === 1;

	const isEngineering = useIsEngineering();

	const portfolios = usePortfolios();
	const portfolioOptions = portfolios?.map((portfolio) => (
		<option key={portfolio.code} value={portfolio.code}>
			{portfolio.name}
		</option>
	)) ?? [];

	const updatePortfolio = (portfolioCode) => {
		dispatch(updateQuotePortfolioCode({ quoteId, portfolioCode })).then(() => {
			window.location.reload();
		});
	};
	
	const uniqueShadeNameOptions = [
		<option key="unique-shade-names" value="unique">
			Unique Shade Names
		</option>,
		<option key="non-unique-shade-names" value="non-unique">
			Non-Unique Shade Names
		</option>,
	];

	const uniqueShadeNameValue = quote.uses_unique_shade_name ? 'unique' : 'non-unique';

	const updateUniqueShadeNames = (e) => {
		const usesUniqueShadeNames = e.target.value === 'unique';
		dispatch(updateQuoteMeta({
			quoteId,
			quoteVM: {
				uses_unique_shade_name: usesUniqueShadeNames,
			}
		}));
	};

	return (
		<>
			<Button
				color={quoteType ?? 'green'}
				fullWidth
				title="Change Quote Level Advanced settings"
				key="advanced-settings"
				onClick={() => setOpen(true)}
			>
				Quote Settings
			</Button>
			<Modal
				key="advanced-setings-modal"
				show={open}
				onHide={() => setOpen(false)}
				fullscreen="sm-down"
				scrollable
			>
				<Modal.Body>
					<Container className="local-bootstrap">
						<Row>
							<Col xs={6}>
								<Row className="mb-4">
									<Col>
										<h5 className={`text-${quoteType}`}>Shipment Selection</h5>
										<Form>
											<Form.Group>
												<FormSelect
													size="sm"
													disabled={!isEditable}
													onChange={(e) => {
														dispatch(updateQuoteMeta({
															quoteId,
															quoteVM: {
																ship_order: e.target.value === "1",
															}
														}));
													}}
												>
													{shippingOptions}
												</FormSelect>
											</Form.Group>
										</Form>
									</Col>
								</Row>
							</Col>
							<Col xs={6}>
								<Row className="mb-4">
									<Col>
										<h5 className={`text-${quoteType}`}>
											Whip Shipment Time Selection
										</h5>
										<Form>
											<Form.Group>
												<FormSelect
													size="sm"
													disabled={!isEditable}
													onChange={(e) => {
														dispatch(updateQuoteMeta({
															quoteId,
															quoteVM: {
																pre_ship_whips: parseInt(e.target.value, 10) === 1
															}
														}));
													}}
												>
													{whipShippingOptions}
												</FormSelect>
											</Form.Group>
										</Form>
									</Col>
								</Row>
							</Col>
							<Col xs={6}>
								<Row className="mb-4">
									<Col>
										<h5 className={`text-${quoteType}`}>Quote Display Price</h5>
										<Form>
											<Form.Group>
												<FormSelect
													size="sm"
													onChange={(e) => {
														dispatch(changeQuoteMultiplier({
															quoteId,
															quoteMultiplier: e.target.value
														}));
													}}
													value={currentPricing}
												>
													{costOptions2}
												</FormSelect>
											</Form.Group>
										</Form>
									</Col>
								</Row>
							</Col>
							<Col xs={6}>
								<Row className="mb-4">
									<Col>
										<h5 className={`text-${quoteType}`}>
											Override Default Light Gap
										</h5>
										<Form>
											<Form.Group>
												<FormSelect
													size="sm"
													value={`${quote?.is_lightgap_choice_active ?? false}`}
													disabled={!isEditable}
													onChange={() => {
														dispatch(updateQuoteMeta({
															quoteId,
															quoteVM: {
																is_lightgap_choice_active:
																!quote?.is_lightgap_choice_active
															}
														}));
														const updatedAssemblies = assemblies.map((a) => ({
															...a,
															balanced_lightgaps: "balanced" as const
														}));
														dispatch(updateAssemblies({
															quoteId,
															assemblies: updatedAssemblies
														}));
													}}
												>
													{lightGapOptions}
												</FormSelect>
											</Form.Group>
										</Form>
									</Col>
								</Row>
							</Col>

							{!hasAccessToUnlockAndReprice ? null : (
								<Col xs={6}>
									<Row className="mb-4">
										<Col>
											<Row>
												<Col xs={10}>
													<h5 className={`text-${quoteType}`}>
														Unlock and Reprice Shades
													</h5>
												</Col>
												<Col xs={2}>
													<button
														type="button"
														className={
															`btn-link hoverable fas fa-info-circle text-${quoteType}`
														}
														style={{
															background: 'none',
															border: 'none',
															padding: 0,
															cursor: 'pointer'
														}}
														onClick={() => {
															ROOT.alert({
																quickFormat: 'info',
																text: 'This will unlock all shades and reprice. '
															+ 'The quote must be unlocked in order to do this.'
															+ 'This can take some time to complete.',
															});
														}}
														aria-label="Info about unlock and reprice"
													/>
												</Col>
											</Row>
											<Button
												color={quoteType ?? 'green'}
												style={{
													paddingLeft: '16px',
													paddingRight: '16px',
													paddingBottom: 0,
													paddingTop: 0,
												}}
												disabled={!isEditable}
												onClick={() => UnlockAndRepriceEachShade()}
												title={
													isEditable
														? 'Reprice all shades'
														: 'The quote needs to be unlocked to use this button.'
												}
											>
												Reprice
											</Button>
										</Col>
									</Row>
								</Col>
							)}
							{!isPsAdmin ? null : (
								<>
									<Col xs={6}>
										<Row className="mb-4">
											<Col>
												<h5 className={`text-${quoteType}`}>
													Override Fabric Railroad
												</h5>
												<Form>
													<Form.Group>
														<FormSelect
															size="sm"
															value={`${quote.is_lightgap_choice_active ?? false}`}
															disabled
															onChange={(e) => {
																dispatch(updateQuoteMeta({
																	quoteId,
																
																	quoteVM: {
																		is_lightgap_choice_active:
																	e.target.value === "true",
																	}
																}));
															}}
															title="Please contact Engineering to activate."
														>
															{railrodPreventionOptions}
														</FormSelect>
													</Form.Group>
												</Form>
											</Col>
										</Row>
									</Col>
									<Col xs={6}>
										<Row className="mb-4">
											<Col>
												<h5 className={`text-${quoteType}`}>
													Previous Quote Id
												</h5>
												<Form>
													<Form.Group>
														<Form.Control
															size="sm"
															type="text"
															disabled={!editingPreviousQuoteId}
															value={localPreviousQuoteId}
															onChange={(e) =>
																setLocalPreviousQuoteId(parseInt(e.target.value, 10))}
														/>
													</Form.Group>
												</Form>
												<Row>
													{editingPreviousQuoteId ? (
														<>
															<Col xs={6}>
																<Button
																	size="sm"
																	style={{
																		paddingBottom: 0,
																		paddingTop: 0,
																	}}
																	color={quoteType ?? 'green'}
																	onClick={localPreviousQuoteCancel}
																>
																	Cancel
																</Button>
															</Col>
															<Col xs={6} className="text-end">
																<Button
																	size="sm"
																	color={quoteType ?? 'green'}
																	onClick={localPreviousQuoteSave}
																>
																	Save
																</Button>
															</Col>
														</>
													) : (
														<Col x={6}>
															<Button
																size="sm"
																color={quoteType ?? 'green'}
																onClick={() =>
																	setEditingPreviousQuoteId(true)}
															>
																Edit
															</Button>
														</Col>
													)}
												</Row>
											</Col>
										</Row>
									</Col>
									<Col xs={6}>
										<Row className="mb-4">
											<Col>
												<h5 className={`text-${quoteType}`}>
													Distributor Select
												</h5>
												<Form>
													<Form.Group>
														<FormSelect
															size="sm"
															disabled={!isEditable}
															onChange={(e) => {
																dispatch(updateQuoteMeta({
																	quoteId,
																	quoteVM: {
																		distributor_id: parseInt(e.target.value, 10),
																		distributor_location_id: 0,
																		representative_id: 0,
																		sales_person_id: 0,
																	}
																
																}));
															}}
															value={distId}
														>
															{distOptions}
														</FormSelect>
													</Form.Group>
												</Form>
											</Col>
										</Row>
									</Col>
									<Col xs={6}>
										<Row className="mb-4">
											<Col>
												<h5 className={`text-${quoteType}`}>
													Distributor Location Select
												</h5>
												<Form>
													<Form.Group>
														<FormSelect
															size="sm"
															disabled={!isEditable}
															onChange={(e) => {
																dispatch(updateQuoteMeta({
																	quoteId,
																	quoteVM: {
																		distributor_location_id:
																	parseInt(e.target.value, 10),
																	}
																}));
															}}
														>
															{distLocationOptions}
														</FormSelect>
													</Form.Group>
												</Form>
											</Col>
										</Row>
									</Col>
									<Col xs={6}>
										<Row className="mb-4">
											<Col>
												<h5 className={`text-${quoteType}`}>
													Representative Select
												</h5>
												<Form>
													<Form.Group>
														<FormSelect
															size="sm"
															disabled={!isEditable}
															onChange={(e) => {
																dispatch(updateQuoteMeta({
																	quoteId,
																	quoteVM: {
																		representative_id: parseInt(e.target.value, 10),
																	}
																}));
															}}
														>
															{repOptions}
														</FormSelect>
													</Form.Group>
												</Form>
											</Col>
										</Row>
									</Col>

									<Col xs={6}>
										<Row className="mb-4">
											<Col>
												<h5 className={`text-${quoteType}`}>
													Salesperson Select
												</h5>
												<Form>
													<Form.Group>
														<FormSelect
															size="sm"
															disabled={!isEditable}
															onChange={(e) => {
																dispatch(updateQuoteMeta({
																	quoteId,
																	quoteVM: {
																		sales_person_id: parseInt(e.target.value, 10),
																	}
																}));
															}}
															value={quote.sales_person_id ?? 0}
														>
															{salespersonItems}
														</FormSelect>
													</Form.Group>
												</Form>
											</Col>
										</Row>
									</Col>

									{(isService || isEngineering) && (<Col xs={6}>
										<Row className="mb-4">
											<Col>
												<h5 className={`text-${quoteType}`}>
													Portfolio Select
												</h5>
												<Form>
													<Form.Group>
														<FormSelect
															size="sm"
															disabled={!isEditable}
															value={quote.portfolio_code}
															onChange={(e) => {
																updatePortfolio(e.target.value);
															}}
														>
															{portfolioOptions}
														</FormSelect>
													</Form.Group>
												</Form>
											</Col>
										</Row>

									</Col>)}
									{(isService || isEngineering) && (<Col xs={6}>
										<Row className="mb-4">
											<Col>
												<h5 className={`text-${quoteType}`}>
													Unique Shade Name Select
												</h5>
												<Form>
													<Form.Group>
														<FormSelect
															size="sm"
															disabled={!isEditable}
															value={uniqueShadeNameValue}
															onChange={updateUniqueShadeNames}
														>
															{uniqueShadeNameOptions}
														</FormSelect>
													</Form.Group>
												</Form>
											</Col>
										</Row>

									</Col>)}
								</>
							)}
							{!isDemo ? (
								<Col xs={6}>
									<Row className="mb-4">
										<Col>
											<h5 className={`text-${quoteType}`}>
												Convert to Demo Quote
											</h5>
											<Button
												onClick={() => demoizeQuote()}
												loading={convertingToDemo}
												color={quoteType ?? "green"}
												disabled={!isEditable}
												title={
													isEditable
														? "Convert this quote to a demo quote"
														: "The quote needs to be unlocked to use this button."
												}
												size="sm"
											>
												Convert
											</Button>
										</Col>
									</Row>
								</Col>
							) : null}
							{
								((isService || isEngineering) && isRma) && (
									<Col xs={6}>
										<Row className="mb-4">
											<Col>

												<h5 className={`text-${quoteType}`}>
													RMA Type
												</h5>
												<Form>
													<Form.Group>
														<FormSelect
															size="sm"
															disabled={!isEditable}
															value={rmaTypeId}
															onChange={(e) => {
																dispatch(updateQuoteMeta({
																	quoteId,
																	quoteVM: {
																		rma_type_id: parseInt(e.target.value, 10),
																	},
																}));
															}}
														>
															{rmaTypeOptions}
														</FormSelect>
													</Form.Group>
												</Form>
											</Col>
										</Row>
									</Col>
								)
							}

						</Row>
					</Container>
				</Modal.Body>
				<Modal.Footer>
					<Button
						title="Close"
						key="advanced-settings-close"
						color={quoteType ?? 'green'}
						onClick={() => setOpen(false)}
					>
						Close
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default QuoteLevelVariables;
